/**
 * AssessmentInfoPanel — brukerhjelp om sammensatt vurdering + vurderingsmatriser.
 *
 * Vises både på /settings/rubrics og som info-boks i Vurderingsoppsett-tab.
 * Kollapsibel — brukerens siste valg huskes i localStorage.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, Info, Award, ListChecks, GraduationCap } from 'lucide-react';

interface Props {
  storageKey?: string;
  defaultOpen?: boolean;
}

export default function AssessmentInfoPanel({ storageKey = 'assessment_info_open', defaultOpen = true }: Props) {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === null) return defaultOpen;
      return stored === 'true';
    } catch { return defaultOpen; }
  });

  function toggle() {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, String(next)); } catch { /* ignore */ }
  }

  return (
    <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between p-4 hover:bg-kf-blue-100 transition-colors"
      >
        <div className="flex items-center gap-2 text-kf-blue-900">
          <Info className="h-5 w-5 flex-shrink-0" />
          <span className="font-semibold text-left">Om vurderingsoppsett og vurderingsmatriser — hva er hva og hvorfor</span>
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-kf-blue-700 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-kf-blue-700 flex-shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-5 pt-1 text-sm text-kf-blue-950 space-y-4">

          {/* To-nivåers forklaring */}
          <section>
            <h3 className="font-semibold flex items-center gap-2 mb-2 text-kf-blue-900">
              <GraduationCap className="h-4 w-4" /> To nivåer av vekting
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-white rounded p-3 border border-kf-blue-100">
                <div className="font-medium text-kf-blue-900 mb-1">Vurderingsoppsett (på kurset)</div>
                <p className="text-xs text-gray-700 mb-2">«Hvordan skal <em>kurset som helhet</em> vurderes?»</p>
                <ul className="text-xs text-gray-700 list-disc list-inside space-y-0.5">
                  <li>Definerer <b>komponenter</b> (skriftlig / praktisk / prosjekt / muntlig …)</li>
                  <li>Vekter mellom komponentene → sluttkarakter</li>
                  <li>Låses per kurs</li>
                </ul>
              </div>
              <div className="bg-white rounded p-3 border border-kf-blue-100">
                <div className="font-medium text-kf-blue-900 mb-1">Vurderingsmatrise (gjenbrukbar)</div>
                <p className="text-xs text-gray-700 mb-2">«Hvordan skal sensor konkret vurdere <em>denne komponenten</em>?»</p>
                <ul className="text-xs text-gray-700 list-disc list-inside space-y-0.5">
                  <li>Definerer <b>kriterier</b> med nivåer og poeng</li>
                  <li>Sensor tvinges til strukturert sjekkliste</li>
                  <li>Kan gjenbrukes på tvers av kurs</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Konkret eksempel */}
          <section>
            <h3 className="font-semibold flex items-center gap-2 mb-2 text-kf-blue-900">
              <ListChecks className="h-4 w-4" /> Eksempel: kurs «Grunnleggende murerprøve»
            </h3>
            <div className="bg-white rounded p-3 border border-kf-blue-100 text-xs text-gray-700 space-y-2">
              <div>
                <div className="font-medium text-kf-blue-900">Vurderingsoppsett (på kurset)</div>
                <table className="mt-1 text-xs">
                  <tbody>
                    <tr><td className="pr-4">Skriftlig teoriprøve</td><td className="font-mono">30 %</td></tr>
                    <tr><td className="pr-4">Praktisk mureoppgave → matrise «Mureprøve v1»</td><td className="font-mono">70 %</td></tr>
                  </tbody>
                </table>
              </div>
              <div>
                <div className="font-medium text-kf-blue-900">Vurderingsmatrise «Mureprøve v1»</div>
                <table className="mt-1 text-xs">
                  <tbody>
                    <tr>
                      <td className="pr-4">Bruker verktøy korrekt</td>
                      <td className="text-gray-600">Utmerket 100 · Godt 80 · Tilstrekkelig 60 · Ikke bestått 0</td>
                    </tr>
                    <tr>
                      <td className="pr-4">Muring følger byggeforskrift</td>
                      <td className="text-gray-600">(samme skala)</td>
                    </tr>
                    <tr>
                      <td className="pr-4">Ryddig arbeidsområde</td>
                      <td className="text-gray-600">(samme skala)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded p-2 mt-2">
                <div className="font-medium text-gray-900 mb-1">Ved sensur av kandidat:</div>
                <ol className="list-decimal list-inside space-y-0.5 text-gray-700">
                  <li>Kandidat tar skriftlig prøve → 80 poeng (auto-scoret)</li>
                  <li>Sensor sensurerer praktisk oppgave via matrisen → f.eks. Utmerket + Godt + Tilstrekkelig = 80 poeng</li>
                  <li>Sluttkarakter beregnes: 80 × 30 % + 80 × 70 % = <b>80 poeng</b></li>
                </ol>
              </div>
            </div>
          </section>

          {/* Med eller uten matrise */}
          <section>
            <h3 className="font-semibold flex items-center gap-2 mb-2 text-kf-blue-900">
              <Award className="h-4 w-4" /> Bør du bruke vurderingsmatrise?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-white rounded p-3 border border-kf-blue-100">
                <div className="font-medium text-gray-900 mb-1">Uten matrise (fri vurdering)</div>
                <ul className="text-xs text-gray-700 list-disc list-inside space-y-0.5">
                  <li>Sensor gir én totalscore + tekstbegrunnelse basert på faglig skjønn</li>
                  <li>Rask og fleksibel</li>
                  <li>Egnet for enkle muntlige eksamener og observasjons-oppgaver</li>
                  <li><span className="text-amber-800">Ulempe:</span> mindre reproduserbart mellom sensorer</li>
                </ul>
              </div>
              <div className="bg-white rounded p-3 border border-emerald-200">
                <div className="font-medium text-emerald-900 mb-1">Med matrise (strukturert vurdering) — anbefalt</div>
                <ul className="text-xs text-gray-700 list-disc list-inside space-y-0.5">
                  <li>Sensor tar stilling per kriterium med definerte nivåer</li>
                  <li>Systemet beregner score konsistent</li>
                  <li>Kandidat vet på forhånd hva som vurderes</li>
                  <li>Reproduserbart mellom sensorer + sporbart til attesten (fryses ved sensur)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Kvalitetssikring */}
          <section className="bg-emerald-50 border border-emerald-200 rounded p-3">
            <div className="font-semibold text-emerald-900 mb-1">Kvalitetssikring for Kompetanseforbundet</div>
            <p className="text-xs text-emerald-900 mb-2">
              Kompetanseattester dokumenterer reell kompetanse — vurderingen må kunne dokumenteres og etterprøves.
              Ved bruk av matrise:
            </p>
            <ul className="text-xs text-emerald-900 list-disc list-inside space-y-0.5">
              <li>Attesten refererer til publisert matrise → tredjepart (arbeidsgiver, NAV, høgskole) kan se <b>hva</b> som ble vurdert</li>
              <li>Klage eller stikkprøve-kontroll får strukturert sensur-spor per kriterium</li>
              <li>To sensorer på samme leveranse gir samme svar hvis de bruker samme matrise</li>
              <li>Matrise-versjon fryses ved sensur — endring senere påvirker ikke utstedt attest</li>
            </ul>
          </section>

          {/* Anbefaling per type */}
          <section>
            <h3 className="font-semibold text-kf-blue-900 mb-2">Anbefaling per vurderingsform</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-600">
                  <tr>
                    <th className="text-left pr-3 py-1">Vurderingsform</th>
                    <th className="text-left pr-3 py-1">Matrise?</th>
                    <th className="text-left py-1">Kommentar</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-t border-kf-blue-100">
                    <td className="pr-3 py-1">Skriftlig prøve</td>
                    <td className="pr-3 py-1"><span className="text-gray-500">Ikke aktuelt</span></td>
                    <td className="py-1">Bruker prøvesett + auto-scoring</td>
                  </tr>
                  <tr className="border-t border-kf-blue-100">
                    <td className="pr-3 py-1">Muntlig eksamen</td>
                    <td className="pr-3 py-1"><span className="text-kf-blue-700">Valgfri</span></td>
                    <td className="py-1">Fri vurdering kan fungere hvis samtalen er kort og eksplorativ</td>
                  </tr>
                  <tr className="border-t border-kf-blue-100">
                    <td className="pr-3 py-1">Praktisk demonstrasjon</td>
                    <td className="pr-3 py-1"><span className="text-emerald-700 font-medium">Anbefalt</span></td>
                    <td className="py-1">Reproduserbar sensur mellom sensorer og kandidater</td>
                  </tr>
                  <tr className="border-t border-kf-blue-100">
                    <td className="pr-3 py-1">Prosjektinnlevering</td>
                    <td className="pr-3 py-1"><span className="text-emerald-700 font-medium">Anbefalt</span></td>
                    <td className="py-1">Flere kriterier å vurdere — struktur reduserer skjønnsmessige avvik</td>
                  </tr>
                  <tr className="border-t border-kf-blue-100">
                    <td className="pr-3 py-1">Mappevurdering</td>
                    <td className="pr-3 py-1"><span className="text-emerald-700 font-medium">Anbefalt</span></td>
                    <td className="py-1">Helhetsvurdering over tid krever eksplisitte kriterier</td>
                  </tr>
                  <tr className="border-t border-kf-blue-100">
                    <td className="pr-3 py-1">Observasjon</td>
                    <td className="pr-3 py-1"><span className="text-emerald-700 font-medium">Anbefalt</span></td>
                    <td className="py-1">Sensor loggfører observasjoner mot kjente kriterier</td>
                  </tr>
                  <tr className="border-t border-kf-blue-100">
                    <td className="pr-3 py-1">Refleksjonsnotat</td>
                    <td className="pr-3 py-1"><span className="text-kf-blue-700">Valgfri</span></td>
                    <td className="py-1">Anbefalt hvis flere sensorer, valgfri ved én sensor</td>
                  </tr>
                  <tr className="border-t border-kf-blue-100">
                    <td className="pr-3 py-1">Gruppearbeid</td>
                    <td className="pr-3 py-1"><span className="text-emerald-700 font-medium">Anbefalt</span></td>
                    <td className="py-1">Individuelle bidrag må vurderes eksplisitt innen felles leveranse</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

        </div>
      )}
    </div>
  );
}
